import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Box } from '@mui/system';
import React, { ReactElement } from 'react';
import useSearchParams from '../../hooks/useSearchParams';

type PickupStatusFilterProps = {
  viewMode?: boolean;
  showAll?: boolean;
};

const options: { label: string; value: string }[] = [
  { label: 'Not ready for pickup', value: 'not_ready' },
  { label: 'Ready for pickup', value: 'ready' },
  { label: 'Scheduled for pickup', value: 'scheduled' },
];

// The pickupStatus URL param is a comma-joined list of status values.
export const parsePickupStatus = (raw?: string): string[] => (raw ? raw.split(',').filter(Boolean) : []);

export default function PickupStatusFilter({ viewMode, showAll }: PickupStatusFilterProps): ReactElement {
  const [params, setURLParams] = useSearchParams();
  const selected = parsePickupStatus(params.pickupStatus);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const next = typeof value === 'string' ? value.split(',') : value;
    setURLParams({ newParams: { pickupStatus: next.filter(Boolean).join(',') } });
  };

  const labelFor = (values: string[]): string =>
    values.length === 0
      ? 'All'
      : options
          .filter((option) => values.includes(option.value))
          .map((option) => option.label)
          .join(', ');

  return viewMode ? (
    <React.Fragment>
      {(selected.length > 0 || showAll) && (
        <Box component="span">
          Status: <strong>{labelFor(selected)}</strong>
        </Box>
      )}
    </React.Fragment>
  ) : (
    <FormControl sx={{ minWidth: 180 }}>
      <InputLabel id="pickup-status-label" size="small" shrink>
        Pickup status
      </InputLabel>
      <Select
        labelId="pickup-status-label"
        multiple
        displayEmpty
        value={selected}
        onChange={handleChange}
        label="Pickup status"
        size="small"
        renderValue={(vals) => labelFor(vals as string[])}
      >
        {options.map((item) => (
          <MenuItem key={item.value} value={item.value}>
            <Checkbox checked={selected.includes(item.value)} size="small" />
            <ListItemText primary={item.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
